import type { ReactNode } from 'react';
import { cn } from './cn';

export interface CardProps {
  /** Optional header title (left side). */
  title?: ReactNode;
  /** Optional header extra (right side — hint text, action, link). */
  extra?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Override the body padding/!layout. */
  bodyClassName?: string;
  /** Body padding density: 'default' = p-4 (16px); 'compact' = p-3 (12px) for nested/inner cards.
   *  Use this instead of a bodyClassName p-3! override. */
  density?: 'default' | 'compact';
  /** Lift + shadow on hover; pair with onClick for clickable cards. */
  hoverable?: boolean;
  onClick?: () => void;
  style?: import('react').CSSProperties;
  /** antd `loading` — shows a skeleton body until content is ready. */
  loading?: boolean;
}

/** Surface container with an optional header (replaces antd Card). */
export function Card({ title, extra, children, className, bodyClassName, density = 'default', hoverable, onClick, style, loading }: CardProps) {
  const hasHeader = title != null || extra != null;
  return (
    <div
      style={style}
      onClick={onClick}
      className={cn(
        'rounded-card border border-line bg-surface shadow-card',
        hoverable && 'transition duration-150 hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-lift',
        (onClick || hoverable) && 'cursor-pointer',
        className,
      )}
    >
      {hasHeader && (
        <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
          <div className="min-w-0 truncate text-base font-semibold text-content">{title}</div>
          {extra != null && <div className="shrink-0 text-xs text-muted">{extra}</div>}
        </div>
      )}
      <div className={cn(density === 'compact' ? 'p-3' : 'p-4', bodyClassName)}>
        {loading ? (
          <div className="space-y-2.5" aria-busy="true">
            {[92, 78, 85, 60].map((w, i) => (
              <div key={i} className="h-3.5 animate-pulse rounded bg-surface-3" style={{ width: `${w}%` }} />
            ))}
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}
